import { t } from '@lingui/macro'
import {
    EmissionFactorWithGasEmissions,
    GasEmissions,
    PassengerTransportationEmissionEstimate,
    PassengerTransportationLegEmissionEstimate,
} from '@lune-climate/lune'
import { PassengerTransportationResolvedLeg } from '@lune-climate/lune/esm/models/PassengerTransportationResolvedLeg'
import { Text } from '@lune-fe/lune-ui-lib'
import { Box } from '@mui/material'
import { FC, useMemo } from 'react'

import { useLuneI18nLocale } from '../../hooks'
import { formatNumbers } from '../../utils/utils'

import EmissionFactorExplanationCard from './EmissionFactorExplanationCard'
import EmissionsRouteExplanationWithMap from './EmissionsRouteExplanationWithMap'
import ExplanationChip from './ExplanationChip'
import ExplanationSteps, { ExplanationText } from './ExplanationSteps'

interface PassengerTransportationEmissionExplanationProps {
    estimate: {
        legs: (PassengerTransportationResolvedLeg | PassengerTransportationLegEmissionEstimate)[]
        requestLegs: PassengerTransportationEmissionEstimate['request']['legs']
    }
    leg: number // 0-indexed
    inferenceDetails?: {
        item: string
        passengers: number
    }
    enableEmissionFactorLink?: boolean
    mapboxAccessToken?: string
    estimateType: 'passenger_transportation' | 'transaction_document'
}

function getDistanceExplanationText(
    requestLeg: PassengerTransportationEmissionEstimate['request']['legs'][number],
): string {
    // TODO: extract from API response (does not have them)
    if ('vehicleType' in requestLeg) {
        return t`Mapbox`
    }

    if ('railType' in requestLeg) {
        // sad
        return t`Mapbox`
    }

    return t`Great Circle Distance`
}

export const PassengerTransportationEmissionExplanation: FC<
    PassengerTransportationEmissionExplanationProps
    // eslint-disable-next-line complexity
> = ({
    estimate,
    leg,
    inferenceDetails,
    enableEmissionFactorLink,
    mapboxAccessToken,
    estimateType,
}) => {
    const requestLeg = estimate.requestLegs[leg]
    const estimateLeg = estimate.legs[leg]
    const locale = useLuneI18nLocale()

    if (!estimateLeg.distance || !estimateLeg.emissionFactor) {
        throw new Error(
            'PassengerTransportationEmissionExplanation does not support estimates without distance or emission factor',
        )
    }

    const distance = estimateLeg.distance
    const emissionFactor = estimateLeg.emissionFactor
    const flightMethodology =
        'methodologyDetails' in estimateLeg && estimateLeg.methodologyDetails?.flight
    const flightRequest =
        'flightIdentification' in requestLeg ? requestLeg.flightIdentification : undefined
    const showMap =
        ((estimateType === 'passenger_transportation' && flightMethodology) ||
            estimateType === 'transaction_document') &&
        'route' in estimateLeg &&
        mapboxAccessToken &&
        flightRequest

    const routeInferenceExplanation = useMemo((): JSX.Element | undefined => {
        if (!inferenceDetails) {
            return undefined
        }
        if (
            !('source' in requestLeg.route) ||
            ((!('code' in requestLeg.route.source) || !('code' in requestLeg.route.destination)) &&
                (!('countryCode' in requestLeg.route.source) ||
                    !('countryCode' in requestLeg.route.destination)))
        ) {
            throw new Error('Unexpected inferred route')
        }
        return (
            <>
                <ExplanationText text={t`Inferred source`} />
                <ExplanationChip
                    text={
                        'code' in requestLeg.route.source
                            ? `${requestLeg.route.source.code}`
                            : `${requestLeg.route.source.streetLine1}, ${requestLeg.route.source.countryCode}`
                    }
                />
                <ExplanationText text={t`and destination`} />
                <ExplanationChip
                    text={
                        'code' in requestLeg.route.destination
                            ? `${requestLeg.route.destination.code}`
                            : `${requestLeg.route.destination.streetLine1}, ${requestLeg.route.destination.countryCode}`
                    }
                />
                <ExplanationText text={t`based on your input`} />
            </>
        )
    }, [inferenceDetails, locale])

    const aircraftExplanation =
        flightMethodology && flightRequest?.flightNumber ? (
            <>
                <ExplanationText text={t`Identified`} />
                <ExplanationChip text={flightMethodology.aircraftName} />
                <ExplanationText text={t`from flight number`} />
                <ExplanationChip text={flightRequest.flightNumber} />
            </>
        ) : flightRequest?.flightNumber && estimateType === 'transaction_document' ? (
            <>
                <ExplanationText text={t`Inferred flight number`} />
                <ExplanationChip text={flightRequest.flightNumber} />
                <ExplanationText text={t`from your input`} />
            </>
        ) : undefined

    const fuelShareExplanation = flightMethodology ? (
        <>
            <ExplanationText text={t`Calculated the shipment's fuel usage share as`} />
            <ExplanationChip
                text={`${formatNumbers(
                    flightMethodology.emissionShare * flightMethodology.flightFuelConsumedTonnes,
                    2,
                )}t`}
            />
        </>
    ) : undefined

    const formattedDistance = formatNumbers(distance.amount, 2)
    const distanceExplanation = (
        <>
            <ExplanationText text={t`Calculated a distance of`} />
            <ExplanationChip text={formattedDistance + ' km'} />
            <ExplanationText text={t`using`} />
            <ExplanationChip text={getDistanceExplanationText(requestLeg)} />
        </>
    )

    const passengerInferenceExplanation = inferenceDetails ? (
        <>
            <ExplanationText text={t`Inferred`} />
            <ExplanationChip text={inferenceDetails.passengers.toString()} />
            <ExplanationText text={t`passenger based on your input`} />
        </>
    ) : undefined

    const emissionFactorExplanation = useMemo((): JSX.Element => {
        return (
            <>
                <ExplanationText text={t`Selected the`} />
                <ExplanationChip text={emissionFactor.name} />
                <ExplanationText text={t`Emission Factor`} />
            </>
        )
    }, [emissionFactor, locale])

    const formulaExplanation = (
        <ExplanationText text={t`Multiplied the Emission Factor by the amount`} />
    )

    const steps: { description?: JSX.Element }[] = [
        ...(inferenceDetails ? [{ description: routeInferenceExplanation }] : []),
        // Don't show distance if we're using the map component as it appears there already
        ...(showMap ? [] : [{ description: distanceExplanation }]),
        ...(aircraftExplanation ? [{ description: aircraftExplanation }] : []),
        ...(fuelShareExplanation ? [{ description: fuelShareExplanation }] : []),
        ...(inferenceDetails ? [{ description: passengerInferenceExplanation }] : []),
        { description: emissionFactorExplanation },
        { description: formulaExplanation },
    ]

    return (
        <>
            <Box>
                <Box>
                    <Text variant={'h5'} sx={{ mb: 4 }}>
                        {t`Methodology`}
                    </Text>
                </Box>
                <ExplanationSteps steps={steps} />
            </Box>
            <Box>
                <EmissionFactorExplanationCard
                    emissionFactor={
                        emissionFactor as Omit<EmissionFactorWithGasEmissions, 'gasEmissions'> & {
                            gasEmissions: GasEmissions
                        }
                    }
                    amount={null}
                    label={emissionFactor.name}
                    href={
                        enableEmissionFactorLink
                            ? `/emission-factors/${emissionFactor.id}`
                            : undefined
                    }
                />
            </Box>
            {showMap && (
                <EmissionsRouteExplanationWithMap
                    mapboxAccessToken={mapboxAccessToken}
                    pdfStructure={false}
                    distanceExplanation={{ description: distanceExplanation }}
                    responseRoute={{
                        source: estimateLeg.route.source,
                        legs: [
                            {
                                location: estimateLeg.route.destination,
                                distance,
                            },
                        ],
                    }}
                    requestRoute={requestLeg.route}
                    requestMethod={flightRequest}
                    distance={distance}
                    routeType={'real'}
                />
            )}
        </>
    )
}

export default PassengerTransportationEmissionExplanation
